import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SlipToggle, { betLegFrom } from "@/components/SlipToggle";
import { useDisplay } from "@/context/DisplayContext";
import TrackToggle from "@/components/TrackToggle";
import { Loader2, Star, Zap, Target, Users, ChevronRight, ShieldCheck, Gauge, Flame, AlertTriangle, EyeOff, Eye } from "lucide-react";

const HIDDEN_KEY = "bb_hidden_fixtures";
const CONF_STEPS = [
  { key: 0, label: "Any confidence" },
  { key: 50, label: "50%+" },
  { key: 60, label: "60%+" },
  { key: 70, label: "70%+" },
  { key: 80, label: "80%+" },
];

const dayKey = (iso) => (iso || "").slice(0, 10);

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

const RISK_META = {
  low: { label: "Low", color: "#34C759", icon: ShieldCheck },
  medium: { label: "Medium", color: "#FF9500", icon: Gauge },
  high: { label: "High", color: "#FF375F", icon: Flame },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "low", label: "Low Risk" },
  { key: "medium", label: "Medium Risk" },
  { key: "high", label: "High Risk" },
];

function PickCard({ p, rank, onOpen, onHide }) {
  const { fmtProb } = useDisplay();
  const top = p.recent.pct === 100;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: rank * 0.03 }}
      onClick={onOpen}
      data-testid="best-bet-card"
      role="button"
      className="glass rounded-3xl p-5 text-left hover:-translate-y-0.5 transition-transform cursor-pointer"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            {p.photo ? (
              <img src={p.photo} alt="" className="h-11 w-11 rounded-full object-cover" />
            ) : (
              <div className="h-11 w-11 rounded-full bg-secondary flex items-center justify-center">
                <Users className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            {rank < 3 && (
              <span className="absolute -top-1 -left-1 h-5 w-5 rounded-full bg-[#FF9500] text-white text-[10px] font-bold flex items-center justify-center shadow">
                {rank + 1}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold truncate">{p.player}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              {p.prop}
              {p.risk && RISK_META[p.risk] && (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5"
                  style={{ color: RISK_META[p.risk].color, backgroundColor: RISK_META[p.risk].color + "22" }}
                >
                  {RISK_META[p.risk].label}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <div className={"font-display font-extrabold text-2xl " + (top ? "text-[#34C759]" : "")}>
            {fmtProb(p.recent.pct)}
          </div>
          {p.odd != null && (
            <span className="text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5 mt-0.5" data-testid="pick-odd">
              @{p.odd.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Zap className="h-3 w-3 text-[#FF9500]" />
          {p.recent.club_form
            ? `${p.recent.hits}~ in ${p.recent.total} club games`
            : `${p.recent.hits} of last ${p.recent.total}`}
        </span>
        {p.vs_opp && (
          <span className="flex items-center gap-1">
            <Target className="h-3 w-3 text-[#0A84FF]" /> {p.vs_opp.hits} of {p.vs_opp.total} vs opp
          </span>
        )}
        {p.starter && (
          <span className="flex items-center gap-1">
            <Star className="h-3 w-3 text-[#AF52DE]" /> {p.starter.hits} of {p.starter.total} as starter
          </span>
        )}
        {p.avail && p.avail.confirmed && (
          <span className="flex items-center gap-1 text-[#34C759] font-medium" data-testid="pick-avail-confirmed">
            <ShieldCheck className="h-3 w-3" /> Confirmed XI
          </span>
        )}
        {p.avail && !p.avail.confirmed && p.avail.rotation_risk && (
          <span className="flex items-center gap-1 text-[#FF9500] font-medium" data-testid="pick-avail-rotation">
            <AlertTriangle className="h-3 w-3" /> Rotation risk · starts {p.avail.start_prob}%
          </span>
        )}
        {p.avail && !p.avail.confirmed && !p.avail.rotation_risk && p.avail.start_prob != null && (
          <span className="flex items-center gap-1" data-testid="pick-avail-start">
            <ShieldCheck className="h-3 w-3 text-[#0A84FF]" /> Starts {p.avail.start_prob}%
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <div className="text-xs min-w-0 truncate">
          <Badge variant="outline" className="rounded-full text-[10px] mr-1.5">
            {p.tournament}
          </Badge>
          <span className="text-muted-foreground">
            {p.home} v {p.away} · {fmtDay(p.date)}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <TrackToggle p={p} />
          <SlipToggle leg={betLegFrom(p)} />
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onHide(p.fixture_id); }}
            data-testid="hide-game-btn"
            title="Hide this game"
            className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
          >
            <EyeOff className="h-3.5 w-3.5" />
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </motion.div>
  );
}

export default function BestBets() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [comp, setComp] = useState("all");
  const [dayF, setDayF] = useState("all");
  const [minConf, setMinConf] = useState(0);
  const [hidden, setHidden] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]")); } catch { return new Set(); }
  });

  const persistHidden = (s) => {
    setHidden(new Set(s));
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...s]));
  };
  const hideGame = (fid) => { const s = new Set(hidden); s.add(fid); persistHidden(s); };
  const unhideAll = () => persistHidden(new Set());

  useEffect(() => {
    api
      .get("/best-bets")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const picks = data?.picks || [];
  const competitions = [...new Set(picks.map((p) => p.tournament))].sort();
  const days = [...new Set(picks.map((p) => dayKey(p.date)))].sort();
  const hiddenCount = picks.filter((p) => hidden.has(p.fixture_id)).length;

  const counts = picks.reduce((a, p) => ({ ...a, [p.risk]: (a[p.risk] || 0) + 1 }), {});
  const shown = picks.filter(
    (p) =>
      (filter === "all" || p.risk === filter) &&
      (comp === "all" || p.tournament === comp) &&
      (dayF === "all" || dayKey(p.date) === dayF) &&
      (p.recent?.pct ?? 0) >= minConf &&
      !hidden.has(p.fixture_id)
  );

  return (
    <div className="space-y-6" data-testid="best-bets-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-[#FF9500]/15 flex items-center justify-center shrink-0">
          <Star className="h-6 w-6 text-[#FF9500]" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Best Bets</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            The highest-confidence player props across your tournaments' upcoming fixtures.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex flex-col items-center justify-center gap-2 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground">
            Scanning upcoming fixtures, this can take a moment the first time.
          </p>
        </div>
      ) : picks.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="best-bets-empty">
          <Star className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No standout bets right now</h3>
          <p className="text-muted-foreground text-sm mt-1">
            We scanned {data?.fixtures_scanned || 0} upcoming fixtures but found no high-confidence props yet.
            Add tournaments by tagging your bets with a competition.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="risk-filters">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              const n = f.key === "all" ? picks.length : counts[f.key] || 0;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`risk-filter-${f.key}`}
                  className={
                    "rounded-full px-4 py-1.5 text-sm font-medium transition-colors " +
                    (active ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground hover:text-foreground")
                  }
                >
                  {f.label} <span className="opacity-70">({n})</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="best-bets-controls">
            <Select value={comp} onValueChange={setComp}>
              <SelectTrigger className="rounded-full h-9 w-auto min-w-[160px] text-sm" data-testid="filter-competition">
                <SelectValue placeholder="Competition" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All competitions</SelectItem>
                {competitions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={dayF} onValueChange={setDayF}>
              <SelectTrigger className="rounded-full h-9 w-auto min-w-[130px] text-sm" data-testid="filter-date">
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any date</SelectItem>
                {days.map((d) => (
                  <SelectItem key={d} value={d}>
                    {new Date(d).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(minConf)} onValueChange={(v) => setMinConf(Number(v))}>
              <SelectTrigger className="rounded-full h-9 w-auto min-w-[150px] text-sm" data-testid="filter-confidence">
                <SelectValue placeholder="Confidence" />
              </SelectTrigger>
              <SelectContent>
                {CONF_STEPS.map((s) => <SelectItem key={s.key} value={String(s.key)}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {hiddenCount > 0 && (
              <button
                onClick={unhideAll}
                data-testid="unhide-all-btn"
                className="flex items-center gap-1.5 rounded-full px-3 h-9 text-sm font-medium bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Eye className="h-3.5 w-3.5" /> Show {hiddenCount} hidden
              </button>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Showing {shown.length} of {picks.length} picks from {data.fixtures_scanned} upcoming fixtures. Tap a pick to open the matchup.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="best-bets-list">
            {shown.map((p, i) => (
              <PickCard
                key={`${p.fixture_id}-${p.player}-${p.prop_key}`}
                p={p}
                rank={i}
                onOpen={() => navigate(`/tournaments/${encodeURIComponent(p.tournament)}`)}
                onHide={hideGame}
              />
            ))}
          </div>
          {shown.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-picks-filtered">
              No picks match these filters. Try widening them{hiddenCount > 0 ? " or showing hidden games" : ""}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
